import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Chip, Avatar, Alert, CircularProgress, Tooltip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete, TextField,
  Stack, Divider, Badge
} from '@mui/material';
import {
  Assignment as AssignIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  MenuBook as SubjectIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { settingsAPI, subjectsAPI, teachersAPI } from '../services/api';
import axios from 'axios';

// ── Assign Teacher Dialog ─────────────────────────────────────────────────────
function AssignDialog({ open, onClose, onSave, row, teachers }) {
  const [teacherId, setTeacherId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTeacherId(row?.teacher_id ? String(row.teacher_id) : '');
  }, [row]);

  const handleSave = async () => {
    setSaving(true);
    await onSave(row, teacherId || null);
    setSaving(false);
  };

  const selected = teachers.find(t => String(t.id) === String(teacherId));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AssignIcon color="primary" />
        Assign Teacher
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {row && (
            <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">Assignment</Typography>
              <Typography fontWeight={600}>{row.class_name}{row.stream ? ` (${row.stream})` : ''} — {row.subject_name}</Typography>
            </Box>
          )}
          <Autocomplete
            options={teachers}
            getOptionLabel={t => `${t.first_name} ${t.last_name} (${t.employee_number || 'N/A'})`}
            value={teachers.find(t => String(t.id) === String(teacherId)) || null}
            onChange={(_, val) => setTeacherId(val ? String(val.id) : '')}
            renderOption={(props, t) => (
              <Box component="li" {...props} sx={{ gap: 1.5 }}>
                <Avatar sx={{ width: 32, height: 32, fontSize: 13, bgcolor: 'primary.main' }}>
                  {t.first_name?.[0]}{t.last_name?.[0]}
                </Avatar>
                <Box>
                  <Typography variant="body2" fontWeight={600}>{t.first_name} {t.last_name}</Typography>
                  <Typography variant="caption" color="text.secondary">{t.subjects || 'No subjects listed'}</Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Select Teacher" size="small" placeholder="Search by name or employee number…" />
            )}
          />
          {selected && (
            <Alert severity="info" icon={<PersonIcon />} sx={{ py: 0.5 }}>
              <strong>{selected.first_name} {selected.last_name}</strong> teaches: {selected.subjects || '(no subjects assigned yet)'}
            </Alert>
          )}
          {!teacherId && (
            <Alert severity="warning" icon={<WarningIcon />} sx={{ py: 0.5 }}>
              Saving with no teacher will <strong>unassign</strong> the current teacher.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : <CheckIcon />}>
          {saving ? 'Saving…' : 'Save Assignment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ClassSubjectAssignments() {
  const { enqueueSnackbar } = useSnackbar();

  const [academicYears, setAcademicYears]   = useState([]);
  const [classes, setClasses]               = useState([]);
  const [subjects, setSubjects]             = useState([]);
  const [teachers, setTeachers]             = useState([]);
  const [assignments, setAssignments]       = useState([]);

  const [yearId, setYearId]     = useState('');
  const [classId, setClassId]   = useState('all');
  const [loading, setLoading]   = useState(false);
  const [dialogRow, setDialogRow] = useState(null);

  // ── Load reference data ───────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      settingsAPI.getAcademicYears(),
      settingsAPI.getClasses(),
      subjectsAPI.getAll(),
      teachersAPI.getAll({ limit: 200 }),
    ]).then(([yrRes, clRes, subRes, tchRes]) => {
      const yrs = yrRes.data.data || [];
      setAcademicYears(yrs);
      setClasses(clRes.data.data || []);
      setSubjects(subRes.data.data || []);
      setTeachers(tchRes.data.data || []);
      const current = yrs.find(y => y.is_current);
      if (current) setYearId(String(current.id));
    });
  }, []);

  // ── Load assignments matrix ───────────────────────────────────────────────
  const loadAssignments = useCallback(async () => {
    if (!yearId) return;
    setLoading(true);
    try {
      const res = await subjectsAPI.getAllAssignments({ academic_year_id: yearId });
      setAssignments(res.data.data || []);
    } catch (err) {
      enqueueSnackbar('Failed to load assignments', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [yearId, enqueueSnackbar]);

  useEffect(() => { if (yearId) loadAssignments(); }, [yearId, loadAssignments]);

  // ── Build full matrix from subject_classes links + existing assignments ─────
  const matrix = React.useMemo(() => {
    const targetClasses = classId === 'all' ? classes : classes.filter(c => String(c.id) === classId);
    const rows = [];

    for (const cls of targetClasses) {
      // subjects linked to this class via subject_classes
      const linkedSubjects = subjects.filter(s =>
        Array.isArray(s.class_ids) && s.class_ids.includes(cls.id)
      );
      if (linkedSubjects.length === 0) continue;

      for (const s of linkedSubjects) {
        // find existing assignment for this class+subject
        const existing = assignments.find(
          a => a.class_id === cls.id && a.subject_id === s.id
        );
        rows.push({
          class_id: cls.id,
          class_name: cls.name,
          stream: cls.stream,
          subject_id: s.id,
          subject_name: s.name,
          subject_code: s.code,
          teacher_id: existing?.teacher_id || null,
          teacher_first: existing?.teacher_first || null,
          teacher_last: existing?.teacher_last || null,
        });
      }
    }

    rows.sort((a, b) => {
      const cls = `${a.class_name}${a.stream || ''}`.localeCompare(`${b.class_name}${b.stream || ''}`);
      return cls !== 0 ? cls : a.subject_name.localeCompare(b.subject_name);
    });
    return rows;
  }, [classes, subjects, assignments, classId]);

  // ── Stats ─────────────────────────────────────────────────────────────────
  const total    = matrix.length;
  const assigned = matrix.filter(r => r.teacher_id).length;
  const missing  = total - assigned;

  // ── Save assignment ───────────────────────────────────────────────────────
  const handleSave = async (row, newTeacherId) => {
    try {
      await subjectsAPI.assignToClass({
        subject_id: row.subject_id,
        class_id: row.class_id,
        teacher_id: newTeacherId || null,
        academic_year_id: parseInt(yearId),
      });
      enqueueSnackbar('Assignment saved successfully', { variant: 'success' });
      setDialogRow(null);
      loadAssignments();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Failed to save assignment', { variant: 'error' });
    }
  };

  // ── Bulk auto-assign (match teacher_subjects to class_assignments) ────────
  const handleAutoAssign = async () => {
    const unassigned = matrix.filter(r => !r.teacher_id);
    if (!unassigned.length) { enqueueSnackbar('All subjects already have teachers assigned', { variant: 'info' }); return; }

    let count = 0;
    for (const row of unassigned) {
      // find a teacher who teaches this subject
      const teacher = teachers.find(t => {
        const teacherSubjects = t.subjects || '';
        return teacherSubjects.split(', ').some(s => s.toLowerCase() === row.subject_name.toLowerCase());
      });
      if (teacher) {
        try {
          await subjectsAPI.assignToClass({
            subject_id: row.subject_id,
            class_id: row.class_id,
            teacher_id: teacher.id,
            academic_year_id: parseInt(yearId),
          });
          count++;
        } catch (_) {}
      }
    }

    if (count > 0) {
      enqueueSnackbar(`Auto-assigned ${count} teacher${count > 1 ? 's' : ''} based on subject specialisation`, { variant: 'success' });
      loadAssignments();
    } else {
      enqueueSnackbar('No automatic matches found. Assign teachers manually.', { variant: 'warning' });
    }
  };

  const currentYear = academicYears.find(y => String(y.id) === String(yearId));

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignIcon color="primary" /> Class Subject Assignments
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Assign a teacher to each subject per class stream. This drives what subjects appear in Marks Entry and Class Register.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadAssignments} disabled={loading}>
            Refresh
          </Button>
          <Button variant="outlined" size="small" color="secondary" startIcon={<PersonAddIcon />} onClick={handleAutoAssign} disabled={loading || !matrix.length}>
            Auto-Assign
          </Button>
        </Stack>
      </Box>

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={5}>
            <FormControl fullWidth size="small">
              <InputLabel>Academic Year</InputLabel>
              <Select value={yearId} onChange={e => setYearId(e.target.value)} label="Academic Year">
                {(academicYears.some(y => y.is_current) ? academicYears.filter(y => y.is_current) : academicYears).map(y => (
                  <MenuItem key={y.id} value={String(y.id)}>
                    {y.year_label} — Term {y.term} (Current)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={5}>
            <FormControl fullWidth size="small">
              <InputLabel>Class</InputLabel>
              <Select value={classId} onChange={e => setClassId(e.target.value)} label="Class">
                <MenuItem value="all">All Classes</MenuItem>
                {classes.map(c => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.name}{c.stream ? ` (${c.stream})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">Progress</Typography>
              <Typography variant="body2" fontWeight={600}>
                {assigned}/{total} assigned
                {missing > 0 && <Chip label={`${missing} missing`} size="small" color="warning" sx={{ ml: 1, height: 18, fontSize: 10 }} />}
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Info box */}
      <Alert severity="info" sx={{ mb: 2 }} icon={<SchoolIcon />}>
        <strong>How it works:</strong> For each class + subject row, click <em>Assign</em> to link a teacher.
        Once assigned, that teacher will see this subject in their <strong>Class Register</strong> and <strong>Marks Entry</strong> when logged in.
        Use <strong>Auto-Assign</strong> to automatically match teachers based on their subject specialisation.
      </Alert>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : matrix.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
          <SubjectIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            {!yearId ? 'Select an academic year to view assignments.' : 'No subjects found for the selected class/year. Make sure subjects are assigned to classes first.'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700 }}>Class</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Subject</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Assigned Teacher</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                let lastClass = null;
                return matrix.map((row, idx) => {
                  const classLabel = `${row.class_name}${row.stream ? ` (${row.stream})` : ''}`;
                  const showClassHeader = classLabel !== lastClass;
                  lastClass = classLabel;
                  return (
                    <React.Fragment key={`${row.class_id}-${row.subject_id}`}>
                      {showClassHeader && (
                        <TableRow sx={{ bgcolor: 'primary.50' }}>
                          <TableCell colSpan={4} sx={{ py: 0.75, borderBottom: '2px solid', borderColor: 'primary.100' }}>
                            <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <SchoolIcon sx={{ fontSize: 14 }} /> {classLabel}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow hover>
                        <TableCell sx={{ pl: 4 }}>
                          <Typography variant="body2" color="text.secondary" fontSize={13}>
                            {classLabel}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <SubjectIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                            <Box>
                              <Typography variant="body2" fontWeight={500}>{row.subject_name}</Typography>
                              {row.subject_code && (
                                <Typography variant="caption" color="text.secondary">{row.subject_code}</Typography>
                              )}
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {row.teacher_id ? (
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: 'success.main' }}>
                                {row.teacher_first?.[0]}{row.teacher_last?.[0]}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {row.teacher_first} {row.teacher_last}
                                </Typography>
                                <Chip label="Assigned" size="small" color="success" variant="outlined" sx={{ height: 16, fontSize: 10 }} />
                              </Box>
                            </Stack>
                          ) : (
                            <Chip
                              label="Not assigned"
                              size="small"
                              color="warning"
                              variant="outlined"
                              icon={<WarningIcon />}
                              sx={{ fontSize: 11 }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={row.teacher_id ? 'Change teacher' : 'Assign teacher'}>
                            <Button
                              size="small"
                              variant={row.teacher_id ? 'outlined' : 'contained'}
                              color={row.teacher_id ? 'inherit' : 'primary'}
                              startIcon={<PersonAddIcon />}
                              onClick={() => setDialogRow(row)}
                              sx={{ minWidth: 100, fontSize: 12 }}
                            >
                              {row.teacher_id ? 'Change' : 'Assign'}
                            </Button>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                });
              })()}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Assign Dialog */}
      <AssignDialog
        open={!!dialogRow}
        onClose={() => setDialogRow(null)}
        onSave={handleSave}
        row={dialogRow}
        teachers={teachers}
      />
    </Box>
  );
}
